import type { PlaybackLimitConfig } from "./playbackLimit/types";
export type { PlaybackLimitConfig } from "./playbackLimit/types";
import type {
  PlayerEnginePlaybackState,
  PlayerEngineProgress,
} from "./audio/types";
import type { CatalogCollectionType, CollectionType, Song, SongId } from "./domain/types";
import type { ActiveQueue, PlayerRepeatMode } from "./utils/queue.types";
export type { ActiveQueue, PlayerRepeatMode };

export type PlayerQueueType = "foryou" | "playlist";

export enum SongQueueRepeatMode {
  OFF = "off",
  QUEUE = "queue",
  TRACK = "track",
}

export type PlayerPlaybackState = PlayerEnginePlaybackState | null;

export type PlayerProgress = PlayerEngineProgress;

export type PlaybackStatus =
  | "IDLE"
  | "LOADING"
  | "READY"
  | "BUFFERING"
  | "PLAYING"
  | "PAUSED"
  | "ERROR";

export interface PlayerState {
  activeQueue: ActiveQueue | null;
  activeQueueType: "foryou" | "playlist" | null;
  activeSong: Song | null;
  activeSongId: SongId | null;
  activeQueueItemId: string | null;
  activeCollectionType: CollectionType | null;
  activeCollectionId: string | null;
  
  status: PlaybackStatus;
  isPlaying: boolean;
  playbackState: PlaybackStatus; // For legacy support
  isBuffering: boolean;
  progress: PlayerProgress;
  // bufferedPosition removed — now in shared values (progressState.buffered)
  
  repeatMode: PlayerRepeatMode;
  shuffleOn: boolean;
  
  canSkip: boolean;
  canSkipReason: "ad" | "skip" | "none";
  
  isQueueEmpty: boolean;
  isQueueLoading: boolean;
  isLoadingMore: boolean;
  
  playbackLimitConfig: PlaybackLimitConfig;
  hasReachedPlaybackLimit: boolean;
  isAutoPlayEnabled: boolean;
}

export interface PlayerActions {
  play: () => Promise<void>;
  pause: () => Promise<void>;
  togglePlay: () => void;
  seekTo: (time: number) => Promise<void>;
  
  startSeek: () => void;
  seekPreview: (time: number) => void;
  endSeek: (time: number) => Promise<void>;
  
  next: () => void;
  previous: () => void;
  jumpTo: (qid: string) => void;
  playFromQueueItem: (qid: string, options?: { autoPlay?: boolean }) => void;
  playFromCollection: (args: {
    songId: SongId;
    collectionType: CatalogCollectionType;
    collectionId: string;
    songs?: Song[];
    options?: { autoPlay?: boolean };
  }) => void;
  
  toggleShuffle: () => void;
  setShuffleOn: (on: boolean) => void;
  setRepeatMode: (mode: PlayerRepeatMode) => void;
  cycleRepeatMode: () => void;
  setAutoPlayEnabled: (enabled: boolean) => void;
  setActiveQueueType: (type: "foryou" | "playlist") => void;
  
  addNext: (songId: string) => Promise<void>;
  removeFromQueue: (queueItemIds: string[]) => void;
  setUpcomingQueueItemIds: (queueItemIds: string[]) => void;
}

export type UsePlayerResult = PlayerState & PlayerActions;
